import { getTypedKeys } from '@sortera/ee-utils'
// eslint-disable-next-line no-restricted-imports
import type { RouteLocation, RouteRecordRaw } from 'vue-router'

import { t } from '@/services/translation/index.ts'
import { useUser } from '@/services/use-user.ts'
import ViewLayout from '@/views/layout/ViewLayout.vue'

import { MASKED_ROUTE_PATH, ROUTE_NAMES, ROUTER_ERROR_MESSAGE_403, ROUTER_ERROR_MESSAGE_NO_ROLE } from './constants.ts'

export const routes: RouteRecordRaw[] = [
  // These auth helper pages will never be reached via the router.
  // Adding these only here to indicate that the URLs are taken.
  // We are rewriting to the statically hosted .html files,
  // that can be found under the same path in the public folder.
  // Locally, the dev server is taking care of the rewrite.
  // When deploying to a web server, a reverse proxy (independent or built in web server)
  // should handle directing incoming traffic to the proper files if they match these path
  { path: '/__/auth/handler', name: ROUTE_NAMES.AUTH_HANDLER, redirect: '' },
  { path: '/__/auth/iframe', name: ROUTE_NAMES.AUTH_IFRAME, redirect: '' },

  {
    // If 3rd party cookies are blocked, we can't use the idToken from the firebase auth flow.
    // The user need to login one more time, directly with MSAL.
    // Therefore we have this separate redirect page.
    path: '/autentisering/logga-in',
    name: ROUTE_NAMES.AUTH_MSAL,
    component: () => import('@/views/auth/ViewAuthMsalRedirect.vue'),
    meta: { authRequired: false, title: () => t('auth.signing-in') },
  },

  // WRAPPED IN MAIN LAYOUT
  {
    path: '/',
    component: ViewLayout,
    meta: {
      authorizeNavigation({ user }) {
        const roleNames = getTypedKeys(user?.roles ?? {})
        const hasNoRoles = roleNames.length === 0 || roleNames.every(key => !user?.roles[key])
        if (hasNoRoles) {
          console.error('User must have a role')
          return new Error(ROUTER_ERROR_MESSAGE_NO_ROLE)
        }
        return undefined
      },
    },
    // all children wrapped in layout
    children: [
      {
        path: '/',
        name: ROUTE_NAMES.HOME,
        meta: { authRequired: true, title: () => t('home.title') },
        component: () => import('@/views/station-list/ViewStationList.vue'),
      },
      { path: '/overview', redirect: '/' },
      {
        path: '/settings',
        name: ROUTE_NAMES.SETTINGS,
        meta: { authRequired: true, title: () => t('settings.title') },
        component: () => import('@/views/settings/ViewSettings.vue'),
      },
      {
        path: '/facility/:facilityId',
        component: () => import('@/views/current-station-provider/ViewCurrentStationProvider.vue'),
        meta: {
          authRequired: true,
        },
        redirect: to => ({ name: ROUTE_NAMES.OVERVIEW, params: to.params }),
        children: [
          {
            path: 'overview',
            name: ROUTE_NAMES.OVERVIEW,
            meta: { authRequired: true },
            component: () => import('@/views/station/ViewStation.vue'),
          },
          {
            path: 'plot/:plotName',
            name: ROUTE_NAMES.PLOT,
            meta: { authRequired: true },
            component: () => import('@/views/plot/ViewPlot.vue'),
            children: [
              {
                path: ':entryId',
                name: ROUTE_NAMES.PLOT_ORDER,
                meta: {
                  authRequired: true,
                  title: (to: RouteLocation) => {
                    const { entryId, plotName } = to.params
                    return `${t('plot.title')} ${plotName} (${entryId})`
                  },
                  navTitle: (to: RouteLocation) => {
                    const { plotName } = to.params
                    return `${t('plot.title')} ${plotName}`
                  },
                },
                component: () => import('@/views/station/ViewPlotEntry.vue'),
              },
            ],
          },
          {
            path: 'manage',
            name: ROUTE_NAMES.MANAGE_STATION,
            meta: {
              title: () => t('station.manage.title'),
              authorizeNavigation({ user }) {
                if (!user?.roles.admin) {
                  return new Error(ROUTER_ERROR_MESSAGE_403)
                }
                return undefined
              },
            },
            component: () => import('@/views/station-manage/ViewManageStation.vue'),
          },
        ],
      },
      {
        path: '/finance',
        name: ROUTE_NAMES.SUMMARIES,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.roles.invoicing) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: () => t('invoicing.title'),
        },
        component: () => import('@/views/finance-list/ViewFinanceList.vue'),
      },
      {
        path: '/finance/order/:orderId',
        name: ROUTE_NAMES.SUMMARY,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.roles.invoicing) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: (to: RouteLocation) => {
            const { orderId } = to.params
            return `${t('order.title')} ${orderId}`
          },
        },
        component: () => import('@/views/finance-order/ViewFinanceOrder.vue'),
      },
      {
        path: '/kpi',
        name: ROUTE_NAMES.KPI,
        meta: { authRequired: true, title: () => t('kpi.title') },
        component: () => import('@/views/kpi/ViewKpi.vue'),
      },
      {
        path: '/convert',
        name: ROUTE_NAMES.CONVERT,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.roles.admin && !user?.roles.workLeader) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: () => t('convert.title'),
        },
        component: () => import('@/views/convert-to-parked/ViewConvertToParked.vue'),
      },
      {
        path: '/manage-user-roles',
        name: ROUTE_NAMES.ADMIN,
        meta: {
          authorizeNavigation({ user }) {
            if (!user?.roles.admin) {
              return new Error(ROUTER_ERROR_MESSAGE_403)
            }
            return undefined
          },
          authRequired: true,
          title: () => t('admin.title'),
        },
        component: () => import('@/views/admin/ViewAdmin.vue'),
      },
    ],
  },

  // OUTSIDE MAIN LAYOUT

  {
    path: '/dev',
    name: ROUTE_NAMES.DEV,
    component: () => import('@/views/dev/ViewDev.vue'),
    meta: {
      authorizeNavigation({ user }) {
        if (!user?.featureFlags?.developer) {
          return new Error(ROUTER_ERROR_MESSAGE_403)
        }
        return undefined
      },
      authRequired: true,
      title: () => 'Dev page',
    },
  },

  {
    // Catch all remaining and display 404 view
    // Must come before the remaining unreachable routes,
    // otherwise they would get matched first.
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR,
    component: () => import('@/views/error/ViewError.vue'),
    props: { title: t('error.status.404') },
    meta: { title: () => t('error.status.404') },
  },

  {
    // not reachable by URL, but can be rendered programmatically by name
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_NO_ROLE,
    component: () => import('@/views/error/ViewError.vue'),
    props: {
      title: t('error.title-no-role'),
      subtitle: `${t('error.subtitle-no-role')} ${t('error.contact', ['fv@sortera.se'])}`,
    },
    meta: { authRequired: true, title: () => t('error.title-no-role') },
  },

  {
    // not reachable by URL, but can be rendered programmatically by name
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_403,
    meta: { title: () => t('error.status.403') },
    component: () => import('@/views/error/ViewError.vue'),
    props: () => {
      const userEmail = useUser().user.value?.email
      return {
        title: t('error.status.403'),
        subtitle: !userEmail ? null : `${t('home.subtext')} ${userEmail}`,
      }
    },
  },

  {
    // not reachable by URL, but can be rendered programmatically by name
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_404,
    meta: { title: () => t('error.status.404') },
    component: () => import('@/views/error/ViewError.vue'),
    props: { title: t('error.status.404') },
  },

  {
    // not reachable by URL, but can be rendered programmatically by name.
    // Props must be provided when navigating to this route.
    path: MASKED_ROUTE_PATH,
    name: ROUTE_NAMES.ERROR_GENERIC,
    meta: { title: () => t('error.error') },
    component: () => import('@/views/error/ViewError.vue'),
    // Allows props to be passed to the error page via query params
    // e.g. '/?error=that+went+wrong'
    props: route => ({ subtitle: route.query.error }),
  },
]
